import { ExportOutlined } from "@ant-design/icons";
import { githubAppManifest } from "@p0security/integrations-github-ai-shared/manifest";
import type { GithubAiComponents } from "@p0security/integrations-github-ai-shared/types";
import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { Button, CommandDisplay, Typography } from "@p0security/ui";
import { mapValues, omit } from "lodash-es";
import type { ReactNode } from "react";
import type React from "react";
import { useMemo } from "react";

import {
  type InstallContext,
  awsStorePrivateKey,
  awsTf,
  gcpStorePrivateKey,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  GithubAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  header(): ReactNode;
  command(
    context: InstallContext,
    organization: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string;
  storePrivateKey(
    organization: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string;
};

const awsSmIac: CredentialIac = {
  header: () =>
    'Apply the following terraform to deploy P0\'s GitHub connector, then click "Next".',
  command: awsTf,
  storePrivateKey: awsStorePrivateKey,
} satisfies CredentialIac<"aws-sm">;

const gcpSmIac: CredentialIac = {
  header: () =>
    'Apply the following terraform to deploy P0\'s GitHub connector, then click "Next".',
  command: gcpTf,
  storePrivateKey: gcpStorePrivateKey,
} satisfies CredentialIac<"gcp-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  header: () => undefined,
  command: () => "",
  storePrivateKey: () => "",
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const AppInstall: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const appInstallUrl = useMemo(() => {
    const resolvedManifest = githubAppManifest(id);
    return `https://github.com/organizations/${id}/settings/apps/new?${new URLSearchParams(
      mapValues(
        {
          // Certain manifest items are not used directly when using query parameters
          ...omit(resolvedManifest, [
            "hook_attributes",
            "default_events",
            "default_permissions",
          ]),
          // ...instead transform them into their respective values
          ...resolvedManifest["default_permissions"],
          webhook_active: resolvedManifest["hook_attributes"].active,
          // ...except we don't use events
        },
        (v) => v.toString()
      )
    ).toString()}`;
  }, [id]);

  return (
    <div>
      <Paragraph type="secondary">
        Create the GitHub App that P0 will use to grant machines access to the{" "}
        {id} organization.
      </Paragraph>
      <Paragraph type="secondary">
        By default, the app will only allow read accesses (machines will receive
        a subset of these based on their requested access, together with your
        configured access policy). Change the permissions on the linked app
        configuration page to set the maximum grantable permissions.
      </Paragraph>
      <Paragraph>
        <Button
          href={appInstallUrl}
          icon={<ExportOutlined />}
          rel="noopener"
          target="_blank"
          type="link"
        >
          Create access app
        </Button>
      </Paragraph>
      <Paragraph>Private key</Paragraph>
      <Paragraph type="secondary">
        Generate a private key on the app&apos;s settings page, save the
        downloaded file as <code>private-key.pem</code>, and run this command to
        update the secret to the key value. The connector cannot mint access
        tokens until the secret holds the key.
      </Paragraph>
      <CommandDisplay
        commands={credentialIac[secretManager.type].storePrivateKey(
          id,
          secretManager
        )}
      />
      <Paragraph>App ID</Paragraph>
    </div>
  );
};

export const githubAiInstaller: FrontendInstaller<GithubAiComponents> = {
  components: {
    credential: {
      instructions: (context, id, item, _field) => {
        // TODO: error handling if no secret manager
        const { secretManager } = item;
        const iac = credentialIac[secretManager.type];
        return {
          commands: {
            iac: [
              {
                header: iac.header(),
                command: iac.command(context, id, secretManager),
              },
            ],
          },
        };
      },
      items: {
        appId: {
          labeler: (_, id, { secretManager, state }) =>
            state === "configure" && (
              <AppInstall id={id} secretManager={secretManager} />
            ),
        },
        secretManager: {
          options: {
            "aws-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
